import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MaskedLine } from "@/lib/reveal";
import { useSmoothScroll } from "@/lib/smooth-scroll";
import heroStill from "@/assets/therapy-hero-web.jpg";
import heroWindMp4 from "@/assets/therapy-hero-wind.mp4";
import heroWindWebm from "@/assets/therapy-hero-wind.webm";

export function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollTo } = useSmoothScroll();
  const [motionOk, setMotionOk] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  useEffect(() => {
    const conn = (navigator as { connection?: { saveData?: boolean } }).connection;
    const ok =
      window.matchMedia("(min-width: 768px)").matches && !(conn && conn.saveData);
    setMotionOk(ok);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      aria-label="Introduction"
      className="relative flex min-h-[100svh] items-end overflow-hidden"
    >
      <motion.div style={reduce ? undefined : { y }} className="absolute inset-0">
        <img
          src={heroStill}
          alt="Maya, a character in The Inkwell's film At Last, standing in the middle of a highway beneath dramatic blue clouds"
          className="absolute inset-0 h-full w-full object-cover object-center"
          fetchPriority="high"
        />
        {!reduce && motionOk && (
          <video
            poster={heroStill}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label="Maya, a character in The Inkwell's film At Last, standing in the middle of a highway as blue clouds and grass move gently in the wind"
            className="absolute inset-0 h-full w-full object-cover object-center opacity-[0.16]"
          >
            <source src={heroWindWebm} type="video/webm" />
            <source src={heroWindMp4} type="video/mp4" />
          </video>
        )}
        <div className="hero-grade absolute inset-0" aria-hidden="true" />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-inkblack via-inkblack/40 to-transparent"
        />
      </motion.div>

      <motion.div
        style={reduce ? undefined : { opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 sm:px-8 lg:px-12"
      >
        <MaskedLine delay={0.1}>
          <p className="mb-8 flex items-center gap-4 font-mono text-xs tracking-[0.3em] uppercase text-haint">
            <span aria-hidden="true" className="h-px w-10 bg-haint/50" />
            Online throughout California
          </p>
        </MaskedLine>

        <MaskedLine delay={0.28}>
          <p className="max-w-xl font-serif text-xl font-light italic leading-snug text-ivory/75 sm:text-2xl">
            We inherit stories. We survive stories. Eventually, we decide how to
            tell them.
          </p>
        </MaskedLine>

        <h1 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.45}>The story is</MaskedLine>
          <MaskedLine delay={0.58}>already yours.</MaskedLine>
          <MaskedLine delay={0.71}>
            <em className="font-light italic text-ivory">Claim it.</em>
          </MaskedLine>
        </h1>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center justify-between gap-6 border-t border-ivory/25 pt-6"
        >
          <div>
            <p
              data-testid="hero-practical-line"
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-ivory/85"
            >
              California Therapy · Individuals · Couples · Families · Telehealth
            </p>
            <p className="mt-3 max-w-md text-base leading-relaxed text-ivory/70">
              Online therapy for individuals, couples and families throughout
              California.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="#begin"
              data-testid="hero-cta-begin"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("#begin");
              }}
              className="rounded-sm border border-gold px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase text-gold transition-colors duration-300 hover:bg-gold hover:text-inkblack"
            >
              Begin therapy
            </a>
            <a
              href="#work"
              data-testid="hero-cta-work"
              aria-label="Explore the work"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("#work");
              }}
              className="group flex h-12 w-12 items-center justify-center rounded-full border border-ivory/35 text-ivory transition-colors duration-300 hover:border-haint hover:text-haint"
            >
              <ArrowDown
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1"
                aria-hidden="true"
              />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
